// Hook for practice-level task defaults (GET/PATCH) with a simple module-scope cache shared across consumers.
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback } from 'react';
import { callApi } from 'src/utils/apiCall';

// Practice task-defaults endpoint. Currently carries `assign_all_applies_to_lead_tasks`
// (whether the "Assign to all" button on a task also reassigns every other open
// task on the same lead) alongside whatever other practice-level task defaults exist.
const ENDPOINT = (practiceId) => `/api/v1/practices/${practiceId}/task-defaults/defaults/`;

// Module-scope cache, keyed by practiceId, so the reminder popup (UpNextCardV2)
// and the Tasks V2 dashboard menu read/write the SAME defaults object instead of
// each holding a stale independent copy. Subscribers registered per practiceId
// are notified on every load/update so all mounted consumers stay in sync.
const _cache = new Map();
const _subscribers = new Map();

function _subscribe(practiceId, listener) {
    if (!_subscribers.has(practiceId)) _subscribers.set(practiceId, new Set());
    _subscribers.get(practiceId).add(listener);
    return () => _subscribers.get(practiceId)?.delete(listener);
}

function _publish(practiceId, defaults) {
    _cache.set(practiceId, defaults);
    _subscribers.get(practiceId)?.forEach((listener) => listener(defaults));
}

export default function usePracticeTaskDefaults(practiceId) {
    const [defaults, setDefaults] = useState(() => (practiceId ? _cache.get(practiceId) : undefined) ?? null);
    const [loading, setLoading] = useState(!!practiceId && !_cache.has(practiceId));
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!practiceId) return undefined;

        const unsubscribe = _subscribe(practiceId, setDefaults);

        if (_cache.has(practiceId)) {
            setDefaults(_cache.get(practiceId));
            setLoading(false);
            return unsubscribe;
        }

        let cancelled = false;
        setLoading(true);
        (async () => {
            try {
                const res = await callApi(ENDPOINT(practiceId));
                if (cancelled) return;
                if (res.ok) {
                    setError(null);
                    _publish(practiceId, res.data);
                } else {
                    setError(res?.data?.detail || 'Failed to load task defaults');
                }
            } catch (err) {
                if (!cancelled) setError(err?.message || 'Failed to load task defaults');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
            unsubscribe();
        };
    }, [practiceId]);

    // PATCHes the partial payload and merges the server's response into the
    // shared cached defaults (so a field the caller didn't send, but the server
    // returns, is preserved) — mirrors the merge semantics callers expect.
    const update = useCallback(async (partial) => {
        if (!practiceId) return undefined;
        try {
            const res = await callApi(ENDPOINT(practiceId), 'patch', partial);
            if (res.ok) {
                setError(null);
                const merged = { ..._cache.get(practiceId), ...res.data };
                _publish(practiceId, merged);
                return merged;
            }
            setError(res?.data?.detail || 'Failed to update task defaults');
            return undefined;
        } catch (err) {
            setError(err?.message || 'Failed to update task defaults');
            return undefined;
        }
    }, [practiceId]);

    return { defaults, loading, error, update };
}
